import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, User, Flag, Ban, UserCircle, LogOut, X } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  "스팸/광고",
  "욕설/비방",
  "음란/부적절한 내용",
  "사기/금전 요구",
  "사칭/허위 프로필",
  "기타",
];

export default function PrivateChatPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetail, setReportDetail] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("liztv_token");
  const storedUser = localStorage.getItem("liztv_user");
  const userId = storedUser ? JSON.parse(storedUser)?.id : null;
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchRoom = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRoom(await res.json());
    } catch (e) {
      console.error(e);
    }
  }, [roomId, token]);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${roomId}/messages?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [roomId, token]);

  useEffect(() => {
    fetchRoom();
    fetchMessages();
    // Poll for new messages every 3s
    pollRef.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchRoom, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const otherId = room?.participant_ids?.find((pid) => pid !== userId) || null;

  // Fetch block relationship once we know the other user
  useEffect(() => {
    if (!token || !otherId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${otherId}/relationship`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setBlocked((await res.json()).blocked);
      } catch { /* ignore */ }
    })();
  }, [token, otherId]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${roomId}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newMessage.trim() }),
      });
      if (res.ok) {
        setNewMessage("");
        await fetchMessages();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "메시지를 보낼 수 없습니다.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!otherId) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${otherId}/block`, {
        method: blocked ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setBlocked(!blocked);
        toast.success(d.message || (blocked ? "차단 해제되었습니다." : "차단되었습니다."));
      } else {
        toast.error(d.detail || "처리에 실패했습니다.");
      }
    } catch {
      toast.error("처리 중 오류가 발생했습니다.");
    }
  };

  const handleLeave = async () => {
    setMenuOpen(false);
    if (!window.confirm("이 대화방을 나가시겠습니까? 목록에서 사라집니다.")) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${roomId}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("대화방을 나갔습니다.");
        navigate("/received-interests");
      }
    } catch {
      toast.error("처리 중 오류가 발생했습니다.");
    }
  };

  const handleViewProfile = () => {
    if (otherId) navigate(`/timeline/${otherId}`);
  };

  const submitReport = async () => {
    if (!reportReason) { toast.error("신고 사유를 선택해주세요."); return; }
    setReportSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${otherId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: reportReason, detail: reportDetail, room_id: roomId }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(d.message || "신고가 접수되었습니다.");
        setShowReport(false);
        setReportReason("");
        setReportDetail("");
      } else {
        toast.error(d.detail || "신고에 실패했습니다.");
      }
    } catch {
      toast.error("신고 처리 중 오류가 발생했습니다.");
    } finally {
      setReportSubmitting(false);
    }
  };

  const otherUser = room?.participant_names?.find((_, i) => room.participant_ids[i] !== userId) || "상대방";
  const otherUserInfo = room?.other_user;

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-stone-500">로그인이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20">
      <div className="max-w-6xl mx-auto flex h-[calc(100vh-5rem)]">
        {/* === LEFT: 상대 정보 + 액션 패널 (데스크톱) === */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white border-r border-stone-200">
          <div className="p-6 flex flex-col items-center text-center border-b border-stone-100">
            <button onClick={handleViewProfile} className="w-20 h-20 rounded-full bg-burgundy-100 flex items-center justify-center hover:ring-2 hover:ring-burgundy-200 transition-all mb-3" data-testid="chat-partner-avatar">
              <User className="w-9 h-9 text-burgundy-500" />
            </button>
            <h2 className="font-semibold text-stone-800 truncate max-w-full" data-testid="chat-partner-name">{otherUser}</h2>
            {otherUserInfo?.location && (
              <p className="text-xs text-stone-400 truncate max-w-full mt-0.5">{otherUserInfo.location}</p>
            )}
          </div>
          <nav className="p-3 flex flex-col gap-0.5">
            <button onClick={handleViewProfile} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-stone-50 transition-colors" data-testid="menu-view-profile">
              <UserCircle className="w-4 h-4 text-stone-400" /> 프로필 보기
            </button>
            <button onClick={() => setShowReport(true)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-amber-50 transition-colors" data-testid="chat-report-btn">
              <Flag className="w-4 h-4 text-amber-500" /> 신고하기
            </button>
            <button onClick={handleToggleBlock} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-red-50 transition-colors" data-testid="chat-block-btn">
              <Ban className="w-4 h-4 text-red-500" /> {blocked ? "차단 해제" : "차단하기"}
            </button>
            <div className="my-2 border-t border-stone-100" />
            <button onClick={handleLeave} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-500 hover:bg-stone-50 transition-colors" data-testid="menu-leave">
              <LogOut className="w-4 h-4 text-stone-400" /> 대화방 나가기
            </button>
          </nav>
          <div className="mt-auto p-3 border-t border-stone-100">
            <button onClick={() => navigate("/received-interests")} className="flex items-center gap-2 px-3 py-2 text-sm text-stone-400 hover:text-stone-600" data-testid="chat-back-btn">
              <ArrowLeft className="w-4 h-4" /> 목록으로
            </button>
          </div>
        </aside>

        {/* === RIGHT: 대화 === */}
        <div className="flex-1 flex flex-col min-w-0 bg-stone-50">
          {/* 모바일 헤더 (사이드바 숨김 시 액션 노출) */}
          <div className="lg:hidden bg-white border-b border-stone-200 px-2 h-14 flex items-center gap-1.5">
            <button onClick={() => navigate("/received-interests")} className="p-2 hover:bg-stone-100 rounded-lg shrink-0" data-testid="chat-back-btn-mobile">
              <ArrowLeft className="w-5 h-5 text-stone-600" />
            </button>
            <button onClick={handleViewProfile} className="flex items-center gap-2 flex-1 min-w-0 text-left">
              <div className="w-8 h-8 rounded-full bg-burgundy-100 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-burgundy-500" />
              </div>
              <span className="font-semibold text-stone-800 truncate text-sm">{otherUser}</span>
            </button>
            <button onClick={() => setShowReport(true)} className="flex items-center gap-1 px-2 py-1.5 rounded-full text-amber-600 border border-amber-200 text-xs font-medium shrink-0" data-testid="chat-report-btn-mobile">
              <Flag className="w-3.5 h-3.5" /> 신고
            </button>
            <button onClick={handleToggleBlock} className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium border shrink-0 ${blocked ? "text-stone-600 border-stone-300" : "text-red-600 border-red-200"}`} data-testid="chat-block-btn-mobile">
              <Ban className="w-3.5 h-3.5" /> {blocked ? "해제" : "차단"}
            </button>
            <button onClick={handleLeave} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 shrink-0" data-testid="menu-leave-mobile" aria-label="대화방 나가기">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
          {/* 데스크톱 상단바 */}
          <div className="hidden lg:flex bg-white border-b border-stone-200 px-6 h-14 items-center">
            <h2 className="font-semibold text-stone-800">{otherUser}</h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
          {loading ? (
            <div className="text-center py-20 text-stone-400">불러오는 중...</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-20 text-stone-400">
              <p>대화를 시작해보세요!</p>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === userId;
                const showDate = i === 0 || formatDate(msg.created_at) !== formatDate(messages[i - 1]?.created_at);
                return (
                  <div key={msg.id}>
                    {showDate && (
                      <div className="text-center my-4">
                        <span className="text-xs text-stone-400 bg-stone-100 px-3 py-1 rounded-full">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                    )}
                    <div className={`flex ${isMine ? "justify-end" : "justify-start"} gap-2`} data-testid={`chat-message-${msg.id}`}>
                      {!isMine && (
                        <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-4 h-4 text-stone-500" />
                        </div>
                      )}
                      <div className={`max-w-[70%]`}>
                        {!isMine && (
                          <p className="text-xs text-stone-400 mb-1 ml-1">{msg.sender_name}</p>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl ${isMine ? "bg-white border border-stone-200 text-stone-800 rounded-br-md shadow-sm" : "bg-white border border-stone-200 text-stone-700 rounded-bl-md"}`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        <p className={`text-[10px] text-stone-400 mt-0.5 ${isMine ? "text-right mr-1" : "ml-1"}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

          {/* Input */}
          <div className="bg-white border-t border-stone-200">
            <div className="max-w-3xl mx-auto px-4 py-3">
              {blocked ? (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border border-red-100 rounded-full" data-testid="blocked-banner">
                  <span className="text-sm text-red-500">차단한 상대입니다. 메시지를 보낼 수 없습니다.</span>
                  <button onClick={handleToggleBlock} className="text-sm font-semibold text-burgundy-600 hover:text-burgundy-700 shrink-0" data-testid="unblock-inline-btn">
                    차단 해제
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                    placeholder="메시지를 입력하세요..."
                    className="flex-1 px-4 py-2.5 bg-stone-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                    className="w-10 h-10 bg-burgundy-500 hover:bg-burgundy-600 text-white rounded-full flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowReport(false)} data-testid="report-modal-overlay">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-amber-500" />
                <h3 className="font-bold text-stone-800">신고하기</h3>
              </div>
              <button onClick={() => setShowReport(false)} className="p-1.5 hover:bg-stone-100 rounded-lg" aria-label="닫기">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-500"><span className="font-semibold text-stone-700">{otherUser}</span> 님을 신고하는 사유를 선택해주세요.</p>
              <div className="space-y-2">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReportReason(r)}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                      reportReason === r ? "border-burgundy-400 bg-burgundy-50 text-burgundy-700 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"
                    }`}
                    data-testid={`report-reason-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <textarea
                value={reportDetail}
                onChange={(e) => setReportDetail(e.target.value)}
                placeholder="상세 내용 (선택)"
                rows={3}
                className="w-full px-3 py-2.5 bg-stone-50 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 resize-none"
                data-testid="report-detail-input"
              />
            </div>
            <div className="p-5 pt-0 flex gap-3">
              <button onClick={() => setShowReport(false)} className="flex-1 py-2.5 rounded-xl border border-stone-300 text-stone-600 text-sm font-medium hover:bg-stone-50" data-testid="report-cancel-btn">
                취소
              </button>
              <button
                onClick={submitReport}
                disabled={reportSubmitting || !reportReason}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-burgundy-500 to-burgundy-700 text-white text-sm font-semibold disabled:opacity-50 shadow-md hover:shadow-lg transition-all"
                data-testid="report-submit-btn"
              >
                {reportSubmitting ? "접수 중..." : "신고 접수"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
